import { ArrowLeft, Clock, Footprints, MapPin, Route as RouteIcon, Lightbulb, Ticket } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ChristmasWalkCard } from "@/components/ChristmasWalk";
import { DayMiniMap } from "@/components/DayMiniMap";
import { GygTivoliCard, GygTourBand } from "@/components/GetYourGuide";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareBar } from "@/components/ShareBar";
import { CHRISTMAS_WALK_ROUTE, CHRISTMAS_WALK_ITEMLIST } from "@/data/christmasWalk";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/nv0h6ujj_2026-09-01%2020_01_05.749%2B0200.webp";

const TIPS = [
  {
    icon: Clock,
    title: "Start late morning",
    body: "Leave around 11:30 — in November and December it's properly dark by about four, so you'll reach Tivoli with the lights fully on, which is the whole point of ending there. The planner's one-tap version of this walk is timed exactly that way.",
  },
  {
    icon: Lightbulb,
    title: "Markets don't run all season",
    body: "The square markets run from mid-November to 21 December, and close early Monday–Wednesday (19:00). Between the 25th and 26th most of the city shuts down.",
  },
  {
    icon: MapPin,
    title: "Hviids is tiny",
    body: "Copenhagen's oldest wine cellar is a beloved gløgg stop — on December weekends it's packed to the door. Weekday afternoons are the calm way in. Full? The wine shop Private Selection runs a little outdoor stall at Østergade 1, right next door towards Strøget — it looks basic, but the gløgg is alright.",
  },
];

// Page FAQ — answers stay plain strings so the FAQPage schema below reuses them.
const FAQS = [
  {
    q: "How long does the Copenhagen Christmas walk take?",
    a: "The route is about 2.5 km with 8 stops — roughly 3½ hours with market browsing and a gløgg stop, or under an hour if you simply walk it end to end.",
  },
  {
    q: "Where does the Copenhagen Christmas walk start?",
    a: "At the Nyhavn Christmas market by the harbour — the Metro to Kongens Nytorv puts you two minutes away. The walk ends at Tivoli, right by the Central Station, so getting home is easy.",
  },
  {
    q: "Is the Copenhagen Christmas walk free?",
    a: "Yes — the walk itself and every square market on the route (Nyhavn, Kongens Nytorv, Højbro Plads, Gammel Torv, Rådhuspladsen) are free. Only Tivoli at the end charges its normal entrance fee if you go in for the lights.",
  },
  {
    q: "What is the best time to do the Copenhagen Christmas walk?",
    a: "Start around 11:30 — in November and December it's properly dark by about four, so you reach Tivoli with the lights fully on, which is the whole point of ending there. Weekday afternoons are calmer than December weekends.",
  },
  {
    q: "Can I get the Christmas walk route on Google Maps?",
    a: "Yes — hit the 'Open the whole route in Google Maps' button under the route above and it opens in Google Maps as a walking route with every stop as a waypoint. Dropped a stop from the list? The link follows your remaining stops automatically.",
  },
  {
    q: "Does the Copenhagen Christmas walk stop at every market?",
    a: "It stops at the six central ones — Nyhavn, Kongens Nytorv, Højbro Plads, Gammel Torv, Rådhuspladsen and Tivoli's Christmas season — because they line up in one natural route. Christiania's Grey Hall market and Bakken's Christmas weekends sit too far out to walk to; the Christmas guide has their dates.",
  },
  {
    q: "Does the Copenhagen Christmas walk go past the city hall square?",
    a: "Yes — Rådhuspladsen (the City Hall Square) is the second-to-last stop, with its big Christmas market right in front of the town hall. From there it's a five-minute walk down Vesterbrogade to Tivoli.",
  },
  {
    q: "Is Tivoli Gardens really worth it at Christmas?",
    a: "Yes — Christmas is arguably Tivoli's best season: a million lights, decorated stalls, gløgg and the rides running in the dark. Entry costs more than the free square markets, but as the walk's finale it's the one ticket worth paying for.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function ChristmasWalkPage() {
  return (
    <main data-testid="christmas-walk-page" className="pt-16 md:pt-20">
      <Seo
        title="The Christmas Walk in Copenhagen — Markets to Tivoli"
        description="A ready-made Christmas walk through Copenhagen: Nyhavn and Kongens Nytorv markets, gløgg at Hviids Vinstue (1723), Gammel Torv, Rådhuspladsen and Tivoli."
        path="/christmas-walk"
        image={HERO_IMG}
      />
      <PageSchema
        name="The Christmas walk — a ready-made Copenhagen Christmas route"
        description="A ready-made ~2.5 km Christmas walking route from Nyhavn's market via Kongens Nytorv, d'Angleterre, Hviids Vinstue and the Gammel Torv and Rådhuspladsen markets to Tivoli."
        path="/christmas-walk"
        crumb="The Christmas walk"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(CHRISTMAS_WALK_ITEMLIST)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Nyhavn's restaurants lit up with Christmas hearts and garlands in December"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-12 md:pt-28 md:pb-16">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/christmas"
              data-testid="christmas-walk-back"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Christmas in Copenhagen
            </Link>
            <ShareButton testid="christmas-walk-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">
              ONE WALK · MARKETS TO TIVOLI
            </p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            The Copenhagen<br />
            <span className="font-editorial italic font-600">Christmas walk</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">
            The Nyhavn market, past the d'Angleterre's, gløgg at Hviids Vinstue (the wine cellar
            dating back to 1723), the Christmas markets and then Tivoli as darkness falls.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-6">
            {["About 2.5 km", "Roughly 3½ hours with stops", "8 stops", "Mid-November – 21 December"].map((t) => (
              <span
                key={t}
                data-testid={`christmas-walk-fact-${t.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-white/10 border border-white/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm"
              >
                <Footprints size={12} className="text-[#FDE047]" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* the interactive route */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div data-testid="christmas-walk-map" className="mb-6">
            <DayMiniMap items={CHRISTMAS_WALK_ROUTE} heightClass="h-72 md:h-96" />
          </div>
          <ChristmasWalkCard />
          <div className="mt-6 max-w-md">
            <GygTivoliCard testid="christmas-walk-tivoli-ticket" />
          </div>
        </div>
      </section>

      {/* good to know */}
      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
              GOOD TO KNOW
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
              Walk it like a local
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {TIPS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06} y={30}>
                <div
                  data-testid={`christmas-walk-tip-${i}`}
                  className="bg-white border border-zinc-200 rounded-2xl p-6 h-full"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#13141A] text-[#FDE047] shrink-0 mb-4">
                    <t.icon size={18} />
                  </div>
                  <h3 className="font-display font-900 tracking-tight text-xl leading-none">
                    {t.title}
                  </h3>
                  <p className="font-body text-sm text-black/60 mt-2 leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10">
              <Link
                to="/christmas"
                data-testid="christmas-walk-to-christmas"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                Every market, date and event
              </Link>
              <Link
                to="/glogg"
                data-testid="christmas-walk-to-glogg"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                The gløgg guide
              </Link>
              <Link
                to="/christmas-food"
                data-testid="christmas-walk-to-christmas-food"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                Where to eat Christmas food
              </Link>
              <Link
                to="/planner"
                data-testid="christmas-walk-to-planner"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                Build it into your itinerary
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* the one ticket to book — Tivoli Christmas entry + ride pass */}
      <GygTourBand tourId="1427049" label="ENDING AT TIVOLI? BOOK THE TICKET" />

      <ExploreLinks
        testid="explore-christmas-walk"
        links={[
          { label: "Every market, date and event — Christmas in Copenhagen", to: "/christmas" },
          { label: "Warm up on the way — the gløgg guide", to: "/glogg" },
          { label: "Where to eat Christmas food — 16 julefrokost spots", to: "/christmas-food" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="christmas-walk-faq" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Christmas walk questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`christmas-walk-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="The Copenhagen Christmas Walk" />
    </main>
  );
}
